import {
  LayoutDashboard,
  Bot,
  Users,
  UserCog,
  ShieldCheck,
  FileText,
  Shield,
  Brain,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  BarChart3,
  DollarSign,
  Phone,
  Settings,
  MessageCircle,
  Activity,
  Mail,
  FileCode,
  Cpu,
  Link2,
  CloudDownload,
  Menu,
  Lightbulb,
  ChevronDown,
  ChevronRight,
  ThumbsDown,
  ListChecks,
  AlertTriangle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: string | number;
  adminOnly?: boolean;
  nonAdminOnly?: boolean;
  children?: NavItem[];
}

interface NavSection {
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
}

const navSections: NavSection[] = [
  {
    title: 'Core',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
      { label: 'Knowledge Assistants', icon: Bot, path: '/admin/events' },
      { label: 'Users', icon: Users, path: '/admin/users', adminOnly: true },
      { label: 'App Users', icon: UserCog, path: '/admin/app-users', adminOnly: true },
      { label: 'Allowlist', icon: ShieldCheck, path: '/admin/allowlist' },
    ],
  },
  {
    title: 'Content / AI Management',
    items: [
      { label: 'Documents', icon: FileText, path: '/admin/documents' },
      { label: 'Guardrails', icon: Shield, path: '/admin/guardrails' },
      { label: 'Behavioral Prompts', icon: Brain, path: '/admin/behavioral-prompts' },
    ],
  },
  {
    title: 'Insights / Analytics',
    items: [
      { label: 'User Feedback', icon: TrendingUp, path: '/admin/feedback' },
      { label: 'Conversations', icon: MessageSquare, path: '/admin/conversations' },
      // { label: 'Sentiment Analysis', icon: TrendingDown, path: '/admin/sentiment-analysis', adminOnly: true },
      { label: 'User Statistics', icon: BarChart3, path: '/admin/user-statistics' },
      { label: 'Cost Analytics', icon: DollarSign, path: '/admin/cost-analytics' },
      {
        label: 'Self-Improvement', icon: Lightbulb, path: '/admin/self-improvement',
        children: [
          { label: 'Non Answered', icon: ListChecks, path: '/admin/self-improvement' },
          { label: 'Negative Feedback', icon: ThumbsDown, path: '/admin/self-improvement/negative-feedback' },
        ],
      },
    ],
  },
  {
    title: 'System / Support',
    items: [
      { label: 'Callback Requests', icon: Phone, path: '/admin/callback-requests' },
      { label: 'Settings', icon: Settings, path: '/admin/settings' },
    ],
  },
  {
    title: 'Admin Tools',
    adminOnly: true,
    items: [
      { label: 'Chat Test', icon: MessageCircle, path: '/admin/chat-test' },
      { label: 'AI Traces', icon: Activity, path: '/admin/ai-traces' },
      { label: 'Error Logs', icon: AlertTriangle, path: '/admin/error-logs' },
      { label: 'Email Verification', icon: Mail, path: '/admin/email-verification' },
      { label: 'Prompt Management', icon: FileCode, path: '/admin/prompts' },
      { label: 'AI Models', icon: Cpu, path: '/admin/ai-models' },
      { label: 'Model-Module Mapping', icon: Link2, path: '/admin/ai-module-mappings' },
      { label: 'Backup Management', icon: CloudDownload, path: '/admin/backup' },
    ],
  },
];

const SIDEBAR_COLLAPSED_KEY = 'admin.sidebar.collapsed';

interface SidebarProps {
  isAdmin: boolean;
  selfImprovementEnabled?: boolean;
  badgeCounts?: Record<string, number>;
}

export function Sidebar({ isAdmin, selfImprovementEnabled = false, badgeCounts = {} }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  });
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  // Auto-expand parent items whose children match the current path
  useEffect(() => {
    const toExpand = new Set<string>();
    for (const section of navSections) {
      for (const item of section.items) {
        if (item.children?.some(c => location.pathname.startsWith(c.path))) {
          toExpand.add(item.path);
        }
      }
    }
    if (toExpand.size > 0) setExpandedItems(prev => new Set([...prev, ...toExpand]));
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname === path;
  };

  const isParentActive = (item: NavItem) => {
    if (item.children) return item.children.some(c => location.pathname.startsWith(c.path));
    return isActive(item.path);
  };

  const toggleExpand = (path: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  };

  return (
    <aside
      className={`h-screen bg-[#0f1419] text-white border-r border-white/10 transition-all duration-300 flex flex-col flex-shrink-0 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Logo / Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/10 flex-shrink-0">
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <LayoutDashboard className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium truncate">Knowledge Companion</span>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 hover:bg-white/10 rounded-md transition-colors flex-shrink-0"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {navSections.map((section, sectionIdx) => {
          // Skip admin-only sections for non-admins
          if (section.adminOnly && !isAdmin) return null;

          // Filter items based on role
          const visibleItems = section.items.filter((item) => {
            if (item.adminOnly && !isAdmin) return false;
            if (item.nonAdminOnly && isAdmin) return false;
            // Self-Improvement: visible to admin always, or to clients with the feature enabled
            if (item.path === '/admin/self-improvement' && !isAdmin && !selfImprovementEnabled) return false;
            return true;
          });

          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title} className={sectionIdx > 0 ? 'mt-6' : ''}>
              {!isCollapsed && (
                <div className="px-3 mb-2">
                  <p className="text-xs font-medium text-white/40 uppercase tracking-wider">
                    {section.title}
                  </p>
                </div>
              )}
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const badge = badgeCounts[item.path] ?? item.badge;
                  const hasChildren = !!(item.children && item.children.length > 0);
                  const expanded = expandedItems.has(item.path);
                  const parentActive = isParentActive(item);

                  if (hasChildren) {
                    return (
                      <div key={item.path}>
                        {/* Parent toggle button */}
                        <button
                          onClick={() => toggleExpand(item.path)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                            parentActive
                              ? 'bg-blue-600/20 text-white'
                              : 'text-white/70 hover:bg-white/10 hover:text-white'
                          }`}
                          title={isCollapsed ? item.label : undefined}
                        >
                          <Icon className={isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} />
                          {!isCollapsed && (
                            <>
                              <span className="text-sm flex-1 text-left truncate">{item.label}</span>
                              {expanded
                                ? <ChevronDown className="w-3.5 h-3.5 text-white/40" />
                                : <ChevronRight className="w-3.5 h-3.5 text-white/40" />
                              }
                            </>
                          )}
                        </button>
                        {/* Children */}
                        {!isCollapsed && expanded && (
                          <div className="mt-0.5 ml-3 pl-3 border-l border-white/10 space-y-0.5">
                            {item.children!.map(child => {
                              const ChildIcon = child.icon;
                              const childActive = location.pathname === child.path;
                              return (
                                <Link
                                  key={child.path}
                                  to={child.path}
                                  className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md transition-all ${
                                    childActive
                                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                                      : 'text-white/60 hover:bg-white/10 hover:text-white'
                                  }`}
                                >
                                  <ChildIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="text-xs truncate">{child.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all relative ${
                        active
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      title={
                        isCollapsed
                          ? `${item.label}${badge ? ` (${badge} new)` : ''}`
                          : undefined
                      }
                    >
                      <div className="relative flex-shrink-0">
                        <Icon className={isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} />
                        {isCollapsed && badge ? (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full ring-1 ring-[#0f1419]" />
                        ) : null}
                      </div>
                      {!isCollapsed && (
                        <span className="text-sm flex-1 text-left truncate">{item.label}</span>
                      )}
                      {!isCollapsed && badge ? (
                        <span
                          className={`text-xs font-semibold min-w-[20px] text-center px-1.5 py-0.5 rounded-full tabular-nums ${
                            active ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                          }`}
                        >
                          {badge}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
